import { Pressable as Slot_Pressable } from '@rn-primitives/slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Link, type LinkProps } from 'expo-router'
import React, { createContext, useContext, useMemo } from 'react'
import { ActivityIndicator, Pressable, View, type TextProps, type ViewProps } from 'react-native'
import Animated from 'react-native-reanimated'
import z from 'zod/v4'
import { Text } from '~/components/ui/text'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'

export const buttonVariantsSchema = z.union([
  z.literal('default'),
  z.literal('primary'),
  z.literal('secondary'),
  z.literal('destructive'),
  z.literal('outline'),
  z.literal('outline-inverted'),
  z.literal('ghost'),
  z.literal('link'),
  z.literal('link-primary'),
])

export const buttonSizeSchema = z.union([
  z.literal('xs'),
  z.literal('sm'),
  z.literal('default'),
  z.literal('lg'),
  z.literal('icon'),
  z.literal('inline'),
  z.literal('xl'),
])

export type ButtonVariant = z.infer<typeof buttonVariantsSchema>
export type ButtonSize = z.infer<typeof buttonSizeSchema>

const buttonVariants = cva(
  'group/button web:ring-offset-background web:focus-visible:outline-none web:focus-visible:ring-2 web:focus-visible:ring-ring web:focus-visible:ring-offset-2 flex w-auto shrink-0 items-center justify-center self-center rounded-full transition-all duration-150 hover:scale-[1.03] active:scale-[0.97]',
  {
    variants: {
      variant: {
        default: 'border border-primary bg-primary',
        primary: 'border border-accent bg-accent',
        secondary: 'border border-secondary bg-secondary',
        destructive: 'border border-destructive bg-destructive',
        outline:
          'border border-primary bg-transparent hover:border-accent group-hover/button:border-accent group-hover/button:bg-transparent',
        'outline-inverted': 'border border-background bg-transparent',
        ghost: 'hover:bg-muted active:bg-muted',
        link: '',
        'link-primary': '',
      } satisfies Record<ButtonVariant, string>,
      size: {
        xs: 'px-4 py-2',
        sm: 'px-6 py-2.5',
        default: 'px-7 py-3',
        lg: 'px-8 py-4',
        icon: 'size-10 items-center justify-center',
        inline: 'px-0 py-0',
        xl: 'px-14 py-5',
      } satisfies Record<ButtonSize, string>,
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const buttonTextVariants = cva(
  'web:whitespace-nowrap web:transition-colors font-normal text-foreground',
  {
    variants: {
      variant: {
        default: 'text-primary-foreground',
        primary: 'text-accent-foreground',
        destructive: 'text-destructive-foreground',
        outline: 'text-foreground group-hover/button:text-accent',
        'outline-inverted': 'text-background group-hover/button:text-accent',
        secondary: 'text-background group-hover/button:text-accent-foreground',
        ghost: 'font-medium text-foreground group-hover/button:text-accent',
        link: 'text-primary underline group-hover/button:text-accent',
        'link-primary': 'text-accent underline underline-offset-4 group-hover/button:text-primary',
      } satisfies Record<ButtonVariant, string>,
      size: {
        xs: 'gap-2 text-sm/none',
        sm: 'gap-2 text-base/none',
        default: 'gap-2 text-base/none',
        lg: 'gap-3 text-lg/none',
        icon: 'gap-0',
        inline: 'gap-2 text-base/none',
        xl: 'gap-4 text-2xl/none',
      } satisfies Record<ButtonSize, string>,
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

type ButtonVariants = VariantProps<typeof buttonVariants>

const ButtonContext = createContext<ButtonVariants & { disabled: boolean; loading: boolean }>({
  variant: 'default',
  size: 'default',
  disabled: false,
  loading: false,
})

function ButtonLabel({
  children,
  className,
  loadingLabel,
  accessory,
  icon,
  ...props
}: TextProps & {
  loadingLabel?: string
  accessory?: React.ReactNode
  icon?: React.ReactNode
}) {
  const { variant, size, loading } = useContext(ButtonContext)
  const textClass = buttonTextVariants({ variant, size })

  return (
    <Text
      className={cn(
        'group/btn-label pointer-events-none flex select-none flex-row items-center justify-center tabular-nums',
        textClass,
        className
      )}
      disabled={props.disabled}
      {...props}>
      {loading ? (
        <>
          <ActivityIndicator
            size={16}
            color={
              variant === 'default'
                ? designTokens.tertiary
                : variant === 'primary'
                  ? designTokens.accentForeground
                  : designTokens.primary
            }
          />
          {loadingLabel && <Text className={textClass}>{loadingLabel}</Text>}
        </>
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
      {accessory}
    </Text>
  )
}

type ButtonProps = React.ComponentProps<typeof Pressable> &
  ButtonVariants & {
    asChild?: boolean
    loading?: boolean
    loadingLabel?: string
    accessory?: React.ReactNode
  }

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

function Button(btnProps: ButtonProps) {
  const {
    className,
    ref,
    size,
    variant,
    asChild,
    loading,
    loadingLabel,
    children,
    accessory,
    ...props
  } = btnProps

  const Component = asChild && !!children ? Slot_Pressable : AnimatedPressable

  const childContent = useMemo(() => {
    if (typeof children === 'function') {
      return children({
        ...btnProps,
        pressed: false,
        hovered: false,
      })
    }

    return children
  }, [children, btnProps])

  return (
    <ButtonContext.Provider
      value={{
        variant,
        size,
        disabled: props.disabled ?? false,
        loading: loading ?? false,
      }}>
      <Component
        className={cn(
          buttonVariants({ variant, size }),
          props.disabled && 'web:pointer-events-none opacity-50',
          className
        )}
        ref={ref}
        role="button"
        {...props}>
        <ButtonLabel accessory={accessory} loadingLabel={loadingLabel}>
          {childContent}
        </ButtonLabel>
      </Component>
    </ButtonContext.Provider>
  )
}

function FakeButton({
  className,
  ref,
  size,
  variant,
  children,
  loading,
  loadingLabel,
  accessory,
  ...props
}: ButtonProps & ViewProps) {
  return (
    <ButtonContext.Provider
      value={{
        variant,
        size,
        disabled: props.disabled ?? false,
        loading: loading ?? false,
      }}>
      <View
        className={cn(
          buttonVariants({ variant, size, className }),
          props.disabled && 'web:pointer-events-none opacity-50'
        )}
        ref={ref}
        {...props}>
        <ButtonLabel accessory={accessory} loadingLabel={loadingLabel}>
          {children}
        </ButtonLabel>
      </View>
    </ButtonContext.Provider>
  )
}

function LinkButton({
  children,
  variant,
  size,
  className,
  loading,
  loadingLabel,
  accessory,
  ...props
}: ButtonProps & LinkProps) {
  return (
    <ButtonContext.Provider
      value={{
        variant,
        size,
        disabled: props.disabled ?? false,
        loading: loading ?? false,
      }}>
      <Link
        {...props}
        className={cn(
          buttonVariants({ variant, size, className }),
          props.disabled && 'web:pointer-events-none opacity-50'
        )}>
        <ButtonLabel accessory={accessory} loadingLabel={loadingLabel}>
          {children}
        </ButtonLabel>
      </Link>
    </ButtonContext.Provider>
  )
}

export { Button, ButtonLabel, buttonTextVariants, buttonVariants, FakeButton, LinkButton }
export type { ButtonProps }
